import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ProjectStatus } from '../shared/models/project-status.enum';
import { ProjectTimelineService } from './project-timeline.service';
import { Subscription } from 'rxjs';

export interface StatusTransition {
  from: string;
  to: string;
  timestamp: string;
  reason: string;
  userId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectStatusTransitionsService implements OnDestroy {
  private subscriptions: Subscription[] = [];
  private monitoredProjects: Set<string> = new Set();
  private isMonitoring: boolean = false;
  private processingProjects: Set<string> = new Set(); // Evitar procesamiento simultáneo
  private recentTransitions: Map<string, number> = new Map(); // Cache de transiciones recientes

  constructor(
    private afs: AngularFirestore,
    private timelineService: ProjectTimelineService
  ) {
    // Inicialización automática deshabilitada para evitar múltiples ejecuciones
    // this.startAutomaticMonitoring();
  }

  /**
   * Inicia el monitoreo automático de cambios de estado
   */
  startAutomaticMonitoring(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    
    // Limpiar transiciones antiguas cada 30 segundos
    const cleanupInterval = setInterval(() => {
      this.cleanOldTransitions();
    }, 30000);
    
    // Monitorear todos los proyectos en la colección investments
    const subscription = this.afs.collection('investments')
      .snapshotChanges()
      .subscribe(
        (changes) => {
          changes.forEach(change => {
            const projectId = change.payload.doc.id;
            const projectData = change.payload.doc.data() as any;
            
            // Procesar solo cambios relevantes que puedan afectar el estado
            if (change.type === 'modified') {
              // Solo procesar si campos críticos cambiaron
              const criticalFields = ['proyectStatus', 'amountSold', 'blockchain'];
              const hasCriticalChange = criticalFields.some(field => {
                const oldValue = (change.payload.doc.data() as any)[field];
                const newValue = projectData[field];
                return oldValue !== newValue;
              });
              
              if (hasCriticalChange) {
                this.handleAutomaticStatusChange(projectId, projectData, change.payload);
              }
            } else if (change.type === 'added') {
              // Procesar nuevos proyectos
              this.handleAutomaticStatusChange(projectId, projectData, change.payload);
            }
          });
        },
        (error) => {
          console.error('❌ [Transitions] Error en monitoreo automático:', error);
        }
      );

    this.subscriptions.push(subscription);
    
    // Guardar el intervalo para limpiarlo al destruir
    this.subscriptions.push({ unsubscribe: () => clearInterval(cleanupInterval) } as Subscription);
  }

  /**
   * Monitorea un proyecto específico
   */
  monitorSpecificProject(projectId: string): void {
    if (this.monitoredProjects.has(projectId)) {
      return; // Ya está siendo monitoreado
    }

    this.monitoredProjects.add(projectId);

    const subscription = this.afs.collection('investments')
      .doc(projectId)
      .snapshotChanges()
      .subscribe(
        (change) => {
          if (change.type === 'modified') {
            const projectData = change.payload.data() as any;
            this.handleAutomaticStatusChange(projectId, projectData, change.payload);
          }
        },
        (error) => {
          console.error(`❌ [Transitions] Error monitoreando proyecto ${projectId}:`, error);
        }
      );

    this.subscriptions.push(subscription);
  }

  /**
   * Maneja los cambios automáticos de estado
   */
  private async handleAutomaticStatusChange(
    projectId: string, 
    projectData: any, 
    docSnapshot: any
  ): Promise<void> {
    // ✅ Evitar procesamiento simultáneo del mismo proyecto
    if (this.processingProjects.has(projectId)) {
      return;
    }

    // ✅ Verificar si ya se procesó una transición reciente para este proyecto
    const lastProcessed = this.recentTransitions.get(projectId);
    const now = Date.now();
    if (lastProcessed && (now - lastProcessed) < 30000) { // 30 segundos de debounce para transacciones blockchain
      return;
    }

    this.processingProjects.add(projectId);
    this.recentTransitions.set(projectId, now);

    try {
      const currentStatus = projectData.proyectStatus;
      
      
      // ✅ Obtener el estado anterior de manera más confiable
      let previousData = null;
      
      // Intentar obtener el estado anterior del snapshot
      if (docSnapshot.metadata && !docSnapshot.metadata.hasPendingWrites) {
        previousData = docSnapshot.data();
      }
      
      // Si no pudimos obtener el estado anterior, intentar obtenerlo de Firestore
      if (!previousData) {
        try {
          const docRef = this.afs.collection('investments').doc(projectId);
          const doc = await docRef.get().toPromise();
          if (doc && doc.exists) {
            previousData = doc.data();
          }
        } catch (error) {
          return;
        }
      }
      
      // ✅ Verificar si debe cambiar automáticamente a "Financiado"
      const shouldTransitionToFunded = await this.shouldTransitionToFunded(projectData);
      
      if (shouldTransitionToFunded) {
        // Registrar la transición automática
        await this.recordStatusTransition(
          projectId,
          'Financiado',
          'Proyecto financiado al 100% automáticamente',
          'auto-system'
        );

        return;
      }
      
      // ✅ Verificar si debe cambiar automáticamente a "En curso" (2 semanas después de financiado)
      const shouldTransitionToInProgress = await this.shouldTransitionToInProgress(projectData);
      
      if (shouldTransitionToInProgress) {
        // Registrar la transición automática
        await this.recordStatusTransition(
          projectId,
          'En curso',
          'Transición automática: Pasaron 2 semanas desde financiación',
          'auto-system'
        );

        return;
      }
      
      // ✅ Verificar si debe cambiar automáticamente de vuelta a "Activo" (si la financiación baja)
      const shouldTransitionToActive = await this.shouldTransitionToActive(projectData);
      
      if (shouldTransitionToActive) {
        // Registrar la transición automática
        await this.recordStatusTransition(
          projectId,
          'Activo',
          'Transición automática: Financiación bajó por debajo del 100%',
          'auto-system'
        );

        return;
      }
      
      // ✅ Procesar cambios manuales de estado (solo si no es un cambio automático)
      if (previousData) {
        const previousStatus = previousData.proyectStatus;
        
        // Solo procesar si el estado realmente cambió manualmente
        if (currentStatus && previousStatus && currentStatus !== previousStatus) {
          
          // ✅ ALERTA ESPECIAL: Cambio de Financiado a En estudio
          if (previousStatus === 'Financiado' && currentStatus === 'En estudio') {
            console.error(`🚨 [Transitions] ALERTA CRÍTICA: Cambio de Financiado a En estudio detectado para proyecto ${projectId}`);
            console.error(`🚨 [Transitions] Datos completos del proyecto:`, {
              projectId,
              previousStatus,
              currentStatus,
              lastModifiedBy: projectData.lastModifiedBy,
              lastUpdated: projectData.lastUpdated,
              amountSold: projectData.amountSold,
              amountToSell: projectData.amountToSell,
              priceToken: projectData.priceToken,
              blockchain: projectData.blockchain,
              endOfSale: projectData.endOfSale
            });
            
            // Obtener todas las transiciones para debuggear
            try {
              const allTransitions = await this.timelineService.getProjectTimeline(projectId);
              console.error(`🚨 [Transitions] Historial de transiciones:`, allTransitions);
            } catch (error) {
              console.error(`🚨 [Transitions] Error obteniendo historial:`, error);
            }
          }
          
          // Verificar que no sea un cambio automático que acabamos de procesar
          const isAutomaticChange = await this.isAutomaticStatusChange(previousStatus, currentStatus, projectData);
          
          if (!isAutomaticChange) {
            // Verificar que el cambio sea válido (no permitir cambios automáticos a "En estudio")
            if (currentStatus === 'En estudio' && previousStatus !== 'En estudio') {
              
            }
            
            // Determinar la razón del cambio
            const reason = this.getAutomaticStatusChangeReason(previousStatus, currentStatus);
            
            // Registrar la transición manual con userId real o 'admin' como fallback
            const userId = projectData.lastModifiedBy || 'admin';
            
            await this.recordStatusTransition(
              projectId,
              currentStatus,
              reason,
              userId
            );
          }
        }
      }
    } catch (error) {
      console.error(`❌ [Transitions] Error procesando cambio en proyecto ${projectId}:`, error);
    } finally {
      // ✅ Limpiar el proyecto del set de procesamiento
      this.processingProjects.delete(projectId);
    }
  }

  /**
   * Determina la razón del cambio automático de estado
   */
  private getAutomaticStatusChangeReason(fromStatus: string, toStatus: string): string {
    const reasons: { [key: string]: string } = {
      'En estudio→Activo': 'Proyecto activado automáticamente',
      'Activo→Financiado': 'Proyecto financiado al 100%',
      'Financiado→Activo': 'Proyecto reactivado: Financiación bajó por debajo del 100%',
      'Financiado→En curso': 'Inicio automático de ejecución del proyecto',
      'En curso→Repartiendo dividendos': 'Inicio automático de reparto de dividendos',
      'Repartiendo dividendos→Vendido': 'Proyecto vendido automáticamente',
      'Activo→Vendido': 'Proyecto vendido directamente',
      'En estudio→Vendido': 'Proyecto vendido sin activación',
      'Financiado→Vendido': 'Proyecto vendido sin ejecución',
      'En curso→Vendido': 'Proyecto vendido durante ejecución',
      'Vendido→Activo': 'Proyecto reactivado automáticamente',
      'Vendido→En curso': 'Proyecto reactivado y en ejecución'
    };

    const key = `${fromStatus}→${toStatus}`;
    return reasons[key] || `Cambio automático de estado: ${fromStatus} → ${toStatus}`;
  }

  /**
   * Verifica si un cambio de estado es automático o manual
   * @param fromStatus - Estado anterior
   * @param toStatus - Estado nuevo
   * @param projectData - Datos del proyecto
   * @returns true si es un cambio automático, false si es manual
   */
  private async isAutomaticStatusChange(fromStatus: string, toStatus: string, projectData: any): Promise<boolean> {
    
    // Cambios automáticos conocidos:
    
    // 1. Activo → Financiado (cuando alcanza 100%)
    if (fromStatus === 'Activo' && toStatus === 'Financiado') {
      const shouldFund = await this.shouldTransitionToFunded(projectData);
      return shouldFund;
    }
    
    // 2. Financiado → En curso (después de 2 semanas)
    if (fromStatus === 'Financiado' && toStatus === 'En curso') {
      const shouldProgress = await this.shouldTransitionToInProgress(projectData);
      return shouldProgress;
    }
    
    // 3. Financiado → Activo (cuando la financiación baja)
    if (fromStatus === 'Financiado' && toStatus === 'Activo') {
      const shouldActivate = await this.shouldTransitionToActive(projectData);
      return shouldActivate;
    }
    
    // 4. Cualquier cambio a "En estudio" NO es automático
    if (toStatus === 'En estudio') {
      return false;
    }
    
    // 5. Cualquier cambio desde "En estudio" NO es automático (excepto los que ya verificamos)
    if (fromStatus === 'En estudio') {
      return false;
    }
    
    // 6. Cambios a estados finales (Vendido, Repartiendo dividendos) son manuales por defecto
    if (toStatus === 'Vendido' || toStatus === 'Repartiendo dividendos') {
      return false;
    }
    
              // 7. Cambios regresivos NO son automáticos (ej: En curso → Financiado)
    // EXCEPCIÓN: Financiado → Activo SÍ es automático cuando la financiación baja
      const statusOrder = ['En estudio', 'Activo', 'Financiado', 'En curso', 'Repartiendo dividendos', 'Vendido'];
    const fromIndex = statusOrder.indexOf(fromStatus);
    const toIndex = statusOrder.indexOf(toStatus);
    
    if (fromIndex !== -1 && toIndex !== -1 && toIndex < fromIndex) {
      return false;
    }
    
    // Por defecto, asumir que es manual
    return false;
  }

  /**
   * Detiene el monitoreo de un proyecto específico
   */
  stopMonitoringProject(projectId: string): void {
    this.monitoredProjects.delete(projectId);
  }

  /**
   * Detiene todo el monitoreo automático
   */
  stopAutomaticMonitoring(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.monitoredProjects.clear();
    this.isMonitoring = false;
  }

  /**
   * Registra una transición de estado para un proyecto
   * @param projectId - ID del proyecto
   * @param newStatus - Nuevo estado
   * @param reason - Razón del cambio
   * @param userId - ID del usuario que hizo el cambio (opcional)
   */
  async recordStatusTransition(
    projectId: string, 
    newStatus: string, 
    reason: string = '',
    userId: string = 'auto-system'
  ): Promise<void> {
    const projectRef = this.afs.collection('investments').doc(projectId);
    
    try {
      // Obtener estado actual
      const projectDoc = await projectRef.get().toPromise();
      const projectData = projectDoc?.data() || {};
      const currentStatus = (projectData as any).proyectStatus || 'Unknown';
      
      // ✅ ALERTA CRÍTICA: Bloquear cambios de Financiado a En estudio
      if (currentStatus === 'Financiado' && newStatus === 'En estudio') {
        console.error(`🚨 [Transitions] BLOQUEO CRÍTICO: Intento de cambiar de Financiado a En estudio para proyecto ${projectId}`);
        console.error(`🚨 [Transitions] Esta transición NO está permitida automáticamente`);
        console.error(`🚨 [Transitions] userId: ${userId}, reason: ${reason}`);
        return; // Bloquear esta transición
      }
      
      // ✅ ALERTA CRÍTICA: Bloquear cambios de Activo a En estudio
      if (currentStatus === 'Activo' && newStatus === 'En estudio') {
        console.error(`🚨 [Transitions] BLOQUEO CRÍTICO: Intento de cambiar de Activo a En estudio para proyecto ${projectId}`);
        console.error(`🚨 [Transitions] Esta transición NO está permitida automáticamente`);
        console.error(`🚨 [Transitions] userId: ${userId}, reason: ${reason}`);
        console.error(`🚨 [Transitions] projectData:`, JSON.stringify(projectData, null, 2));
        return; // Bloquear esta transición
      }
      
      // ✅ Evitar registrar transiciones cuando el estado no cambia
      if (currentStatus === newStatus) {
        return; // No registrar transición si el estado es el mismo
      }
      
      // ✅ Verificar si ya existe una transición reciente al mismo estado
      const existingTransitions = await this.timelineService.getProjectTimeline(projectId);
      const recentTransition = existingTransitions.find(t => 
        t.to === newStatus && 
        new Date(t.timestamp).getTime() > Date.now() - 10000 // Últimos 10 segundos
      );
      
      if (recentTransition) {
        return;
      }
      
      // Registrar transición en la nueva colección
      await this.timelineService.recordTransition(
        projectId,
        currentStatus,
        newStatus,
        reason,
        userId
      );
      
      // Actualizar solo el estado del proyecto
      await projectRef.update({
        proyectStatus: newStatus,
        lastStatusUpdate: new Date().toISOString()
      });
      
    } catch (error) {
      console.error(`❌ Error registrando transición: ${error}`);
      throw error;
    }
  }

  /**
   * Obtiene la fecha de una transición específica
   * @param project - Datos del proyecto
   * @param fromStatus - Estado de origen
   * @param toStatus - Estado de destino
   * @returns Fecha de la transición o null si no existe
   */
  async getTransitionDate(project: any, fromStatus: string, toStatus: string): Promise<Date | null> {
    if (!project?.id) return null;
    return await this.timelineService.getTransitionDate(project.id, fromStatus, toStatus);
  }

  /**
   * Obtiene la fecha de financiación (transición de Activo a Financiado)
   * @param project - Datos del proyecto
   * @returns Fecha de financiación o null si no existe
   */
  async getFundedDate(project: any): Promise<Date | null> {
    return await this.timelineService.getFundedDate(project.id);
  }

  /**
   * Obtiene la fecha de inicio de "En curso" (transición de Financiado a En curso)
   * @param project - Datos del proyecto
   * @returns Fecha de inicio de "En curso" o null si no existe
   */
  async getInProgressDate(project: any): Promise<Date | null> {
    return await this.timelineService.getInProgressDate(project.id);
  }

  /**
   * Obtiene la fecha de inicio de "Repartiendo dividendos"
   * @param project - Datos del proyecto
   * @returns Fecha de inicio de dividendos o null si no existe
   */
  async getPayingDividendsDate(project: any): Promise<Date | null> {
    const inProgressToDividends = await this.getTransitionDate(project, ProjectStatus.IN_PROGRESS, ProjectStatus.PAYING_DIVIDENDS);
    if (inProgressToDividends) return inProgressToDividends;
    
    const fundedToDividends = await this.getTransitionDate(project, ProjectStatus.FUNDED, ProjectStatus.PAYING_DIVIDENDS);
    if (fundedToDividends) return fundedToDividends;
    
    return await this.getTransitionDate(project, ProjectStatus.ACTIVE, ProjectStatus.PAYING_DIVIDENDS);
  }

  /**
   * Obtiene la fecha de venta (transición a Vendido)
   * @param project - Datos del proyecto
   * @returns Fecha de venta o null si no existe
   */
  async getSoldDate(project: any): Promise<Date | null> {
    const dividendsToSold = await this.getTransitionDate(project, ProjectStatus.PAYING_DIVIDENDS, ProjectStatus.SOLD);
    if (dividendsToSold) return dividendsToSold;
    
    const inProgressToSold = await this.getTransitionDate(project, ProjectStatus.IN_PROGRESS, ProjectStatus.SOLD);
    if (inProgressToSold) return inProgressToSold;
    
    return await this.getTransitionDate(project, ProjectStatus.FUNDED, ProjectStatus.SOLD);
  }

  /**
   * Obtiene el tiempo transcurrido en el estado actual
   * @param project - Datos del proyecto
   * @returns Tiempo en milisegundos o 0 si no se puede determinar
   */
  async getTimeInCurrentStatus(project: any): Promise<number> {
    if (!project?.id) return 0;
    return await this.timelineService.getTimeInCurrentStatus(project.id);
  }

  /**
   * Obtiene todas las transiciones de un proyecto
   * @param project - Datos del proyecto
   * @returns Array de transiciones ordenadas por fecha
   */
  async getAllTransitions(project: any): Promise<StatusTransition[]> {
    if (!project?.id) return [];
    const transitions = await this.timelineService.getProjectTimeline(project.id);
    return transitions.map(t => ({
      from: t.from || '',
      to: t.to,
      timestamp: t.timestamp,
      reason: t.reason,
      userId: t.userId
    }));
  }

  /**
   * Verifica si un proyecto debe cambiar automáticamente a "Financiado"
   * @param project - Datos del proyecto
   * @returns true si debe cambiar a "Financiado"
   */
  async shouldTransitionToFunded(project: any): Promise<boolean> {
    // Si ya está financiado o en un estado posterior, no cambiar
    if (project.proyectStatus === 'Financiado' || 
        project.proyectStatus === 'En curso' || 
        project.proyectStatus === 'Repartiendo dividendos' || 
        project.proyectStatus === 'Vendido') {
      return false;
    }

    // Solo cambiar si está activo
    if (project.proyectStatus !== 'Activo') {
      return false;
    }

    // Verificar si tiene los datos necesarios para calcular el porcentaje
    if (!project.amountToSell || !project.priceToken || !project.amountSold) {
      return false;
    }

    // Calcular el porcentaje vendido
    const targetAmount = project.amountToSell * (project.priceToken / 100);
    const eurosSold = project.amountSold * (project.priceToken / 100);
    const percentageSold = (eurosSold * 100) / targetAmount;

    // Cambiar a "Financiado" si alcanza o supera 100%
    return percentageSold >= 100;
  }

  /**
   * Verifica si un proyecto financiado debe cambiar automáticamente de vuelta a "Activo"
   * @param project - Datos del proyecto
   * @returns true si debe cambiar a "Activo"
   */
  async shouldTransitionToActive(project: any): Promise<boolean> {
    // Solo verificar si está financiado
    if (project.proyectStatus !== 'Financiado') {
      return false;
    }

    // Verificar si tiene los datos necesarios para calcular el porcentaje
    if (!project.amountToSell || !project.priceToken || !project.amountSold) {
      return false;
    }

    // Calcular el porcentaje vendido
    const targetAmount = project.amountToSell * (project.priceToken / 100);
    const eurosSold = project.amountSold * (project.priceToken / 100);
    const percentageSold = (eurosSold * 100) / targetAmount;

    // Cambiar a "Activo" si está por debajo del 100%
    return percentageSold < 100;
  }

  /**
   * Verifica si un proyecto debe cambiar automáticamente a "En curso"
   * @param project - Datos del proyecto
   * @returns true si debe cambiar a "En curso"
   */
  async shouldTransitionToInProgress(project: any): Promise<boolean> {
    // Si ya está en "En curso", no cambiar
    if (project.proyectStatus === ProjectStatus.IN_PROGRESS) {
      return false;
    }

    // Si no está financiado, no cambiar
    if (project.proyectStatus !== ProjectStatus.FUNDED) {
      return false;
    }

    // Obtener fecha de financiación
    const fundedDate = await this.getFundedDate(project);
    if (!fundedDate) {
      return false;
    }

    // Verificar si han pasado 2 semanas (14 días)
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

    return fundedDate <= twoWeeksAgo;
  }

  /**
   * Cambia automáticamente un proyecto financiado de vuelta a "Activo" si la financiación baja
   * @param projectId - ID del proyecto
   * @param project - Datos del proyecto
   * @returns true si se hizo el cambio, false si no
   */
  async autoTransitionToActive(projectId: string, project: any): Promise<boolean> {
    if (await this.shouldTransitionToActive(project)) {
      await this.recordStatusTransition(
        projectId,
        'Activo',
        'Transición automática: Financiación bajó por debajo del 100%',
        'auto-system'
      );
      return true;
    }
    return false;
  }

  /**
   * Cambia automáticamente un proyecto a "En curso" si han pasado 2 semanas
   * @param projectId - ID del proyecto
   * @param project - Datos del proyecto
   * @returns true si se hizo el cambio, false si no
   */
  async autoTransitionToInProgress(projectId: string, project: any): Promise<boolean> {
    if (await this.shouldTransitionToInProgress(project)) {
      await this.recordStatusTransition(
        projectId,
        ProjectStatus.IN_PROGRESS,
        'Transición automática: Pasaron 2 semanas desde financiación',
        'auto-system'
      );
      return true;
    }
    return false;
  }

  /**
   * Obtiene un resumen de las transiciones de un proyecto
   * @param project - Datos del proyecto
   * @returns Objeto con fechas importantes
   */
  async getProjectTimeline(project: any): Promise<{
    activated?: Date;
    funded?: Date;
    inProgress?: Date;
    payingDividends?: Date;
    sold?: Date;
  }> {
    return {
      activated: await this.getTransitionDate(project, ProjectStatus.UNDER_ANALYSIS, ProjectStatus.ACTIVE) || undefined,
      funded: await this.getFundedDate(project) || undefined,
      inProgress: await this.getInProgressDate(project) || undefined,
      payingDividends: await this.getPayingDividendsDate(project) || undefined,
      sold: await this.getSoldDate(project) || undefined
    };
  }

  ngOnDestroy(): void {
    this.stopAutomaticMonitoring();
    this.processingProjects.clear();
    this.recentTransitions.clear();
  }

  /**
   * Limpia transiciones antiguas del cache
   */
  private cleanOldTransitions(): void {
    const now = Date.now();
    const maxAge = 30000; // 30 segundos
    
    for (const [projectId, timestamp] of this.recentTransitions.entries()) {
      if (now - timestamp > maxAge) {
        this.recentTransitions.delete(projectId);
      }
    }
  }
} 